import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, Legend
} from 'recharts';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { 
  BarChart3, TrendingUp, TrendingDown, DollarSign, ShoppingBag, 
  Package, Truck, RefreshCw, Calendar, Users, Boxes, 
  ArrowUpRight, ArrowDownRight, ChevronRight, FileText,
  Receipt, CreditCard, Wallet, PiggyBank, ShoppingCart,
  Clock, Target, CheckCircle, AlertTriangle
} from 'lucide-react';

const COLORS = ['#f97316', '#10b981', '#6366f1', '#ec4899', '#3b82f6', '#eab308'];

const ReportsHub = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('today');
  const [customDate, setCustomDate] = useState({ start: '', end: '' });
  
  // All report data in one state
  const [reportData, setReportData] = useState({
    sales: null,
    purchases: null,
    transfers: null,
    expenses: null,
    summary: null
  });

  useEffect(() => {
    fetchAllReports();
  }, [period, customDate.start, customDate.end]);

  const buildQueryParams = () => {
    let params = `period=${period}`;
    if (period === 'custom' && customDate.start && customDate.end) {
      params += `&start_date=${customDate.start}&end_date=${customDate.end}`;
    }
    return params;
  };

  const fetchAllReports = async () => {
    setLoading(true);
    const params = buildQueryParams();
    const timestamp = Date.now();
    
    try {
      // Fetch all reports in parallel
      const [salesRes, purchasesRes, transfersRes, expensesRes] = await Promise.all([
        api.get(`/reports/hub/sales?${params}&t=${timestamp}`),
        api.get(`/reports/hub/purchases?${params}&t=${timestamp}`),
        api.get(`/reports/hub/transfers?${params}&t=${timestamp}`),
        api.get(`/reports/hub/expenses?${params}&t=${timestamp}`)
      ]);

      setReportData({
        sales: salesRes.data,
        purchases: purchasesRes.data,
        transfers: transfersRes.data,
        expenses: expensesRes.data,
        summary: {
          total_income: salesRes.data?.summary?.total || 0,
          total_expenses: (purchasesRes.data?.summary?.total || 0) + (expensesRes.data?.summary?.total || 0),
          net_flow: (salesRes.data?.summary?.total || 0) - ((purchasesRes.data?.summary?.total || 0) + (expensesRes.data?.summary?.total || 0))
        }
      });
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { 
      style: 'currency', 
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getPeriodLabel = () => {
    const labels = {
      today: 'Hoy',
      week: 'Esta Semana',
      month: 'Este Mes',
      year: 'Este Año',
      custom: 'Personalizado'
    };
    return labels[period] || 'Período';
  };

  const { sales, purchases, transfers, expenses, summary } = reportData;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 pt-16 md:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-chivo font-extrabold flex items-center gap-3" data-testid="reports-hub-title">
              <BarChart3 className="w-8 h-8 text-accent" />
              Centro de Reportes
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Vista consolidada de todas las operaciones del negocio
            </p>
          </div>

          {/* Period Filter Bar */}
          <Card className="mb-6 border-0 shadow-sm bg-white">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Period Buttons */}
                <div className="flex bg-slate-100 rounded-xl p-1.5">
                  {[
                    { id: 'today', label: 'Día', icon: Clock },
                    { id: 'week', label: 'Semana', icon: Calendar },
                    { id: 'month', label: 'Mes', icon: Calendar },
                    { id: 'year', label: 'Año', icon: Calendar },
                  ].map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setPeriod(id)}
                      data-testid={`period-${id}`}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        period === id
                          ? 'bg-accent text-white shadow-md'
                          : 'text-muted-foreground hover:text-foreground hover:bg-white'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Custom Date Range */}
                <div className="flex items-center gap-2 ml-auto">
                  <Input
                    type="date"
                    value={customDate.start}
                    onChange={(e) => {
                      setCustomDate(prev => ({ ...prev, start: e.target.value }));
                      setPeriod('custom');
                    }}
                    className="w-auto h-10"
                    data-testid="date-start"
                  />
                  <span className="text-muted-foreground">a</span>
                  <Input
                    type="date"
                    value={customDate.end}
                    onChange={(e) => {
                      setCustomDate(prev => ({ ...prev, end: e.target.value }));
                      setPeriod('custom');
                    }}
                    className="w-auto h-10"
                    data-testid="date-end"
                  />
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={fetchAllReports} 
                    disabled={loading}
                    className="h-10 w-10"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Period Display */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Mostrando:</span>
                <span className="text-sm font-semibold bg-accent/10 text-accent px-3 py-1 rounded-full">
                  {getPeriodLabel()}
                </span>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-10 h-10 animate-spin text-accent mx-auto mb-3" />
                <p className="text-muted-foreground">Cargando reportes...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Executive Summary Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Total Income */}
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-700">Ingresos Totales</p>
                        <p className="text-3xl font-extrabold text-green-800 mt-1">
                          {formatCurrency(summary?.total_income)}
                        </p>
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <ArrowUpRight className="w-3 h-3" />
                          {sales?.summary?.orders_count || 0} ventas
                        </p>
                      </div>
                      <div className="p-4 bg-green-500/10 rounded-xl">
                        <DollarSign className="w-8 h-8 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Expenses */}
                <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-red-700">Egresos Totales</p>
                        <p className="text-3xl font-extrabold text-red-800 mt-1">
                          {formatCurrency(summary?.total_expenses)}
                        </p>
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <ArrowDownRight className="w-3 h-3" />
                          Compras + Gastos
                        </p>
                      </div>
                      <div className="p-4 bg-red-500/10 rounded-xl">
                        <ShoppingCart className="w-8 h-8 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Net Flow */}
                <Card className={`shadow-sm ${
                  (summary?.net_flow || 0) >= 0 
                    ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200' 
                    : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'
                }`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-medium ${(summary?.net_flow || 0) >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                          Flujo Neto
                        </p>
                        <p className={`text-3xl font-extrabold mt-1 ${(summary?.net_flow || 0) >= 0 ? 'text-blue-800' : 'text-amber-800'}`}>
                          {formatCurrency(summary?.net_flow)}
                        </p>
                        <p className={`text-xs mt-1 flex items-center gap-1 ${(summary?.net_flow || 0) >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                          {(summary?.net_flow || 0) >= 0 ? (
                            <><CheckCircle className="w-3 h-3" /> Positivo</>
                          ) : (
                            <><AlertTriangle className="w-3 h-3" /> Negativo</>
                          )}
                        </p>
                      </div>
                      <div className={`p-4 rounded-xl ${(summary?.net_flow || 0) >= 0 ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}>
                        <TrendingUp className={`w-8 h-8 ${(summary?.net_flow || 0) >= 0 ? 'text-blue-600' : 'text-amber-600'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* VENTAS Card */}
                <Card className="shadow-sm border-0 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-bold">
                      <Receipt className="w-6 h-6" />
                      Ventas
                    </CardTitle>
                    <p className="text-green-100 text-sm">
                      {sales?.summary?.orders_count || 0} órdenes completadas
                    </p>
                  </CardHeader>
                  <CardContent className="p-5 space-y-4">
                    {/* Sales Summary */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Total Ventas</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(sales?.summary?.total)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Ticket Promedio</p>
                        <p className="text-xl font-bold">{formatCurrency(sales?.summary?.avg_ticket)}</p>
                      </div>
                    </div>

                    {/* Sales by Payment Method */}
                    {sales?.by_payment_method && sales.by_payment_method.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          Por Método de Pago
                        </h4>
                        <div className="space-y-2">
                          {sales.by_payment_method.map((method, idx) => (
                            <div key={method.method} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                <span className="text-sm font-medium">{method.label}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-green-600">{formatCurrency(method.total)}</span>
                                <span className="text-xs text-muted-foreground ml-2">({method.count})</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sales by Type */}
                    {sales?.by_order_type && sales.by_order_type.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Por Tipo de Orden
                        </h4>
                        <div className="flex gap-2">
                          {sales.by_order_type.map((type, idx) => (
                            <div key={type.type} className="flex-1 p-3 bg-slate-50 rounded-lg text-center">
                              <p className="text-lg font-bold">{type.count}</p>
                              <p className="text-xs text-muted-foreground">{type.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!sales?.summary?.total || sales.summary.total === 0) && (
                      <div className="text-center py-6 text-muted-foreground">
                        <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No hay ventas en este período</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* COMPRAS Card */}
                <Card className="shadow-sm border-0 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-600 text-white pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-bold">
                      <ShoppingBag className="w-6 h-6" />
                      Compras
                    </CardTitle>
                    <p className="text-orange-100 text-sm">
                      {purchases?.summary?.count || 0} compras registradas
                    </p>
                  </CardHeader>
                  <CardContent className="p-5 space-y-4">
                    {/* Purchases Summary */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Total Compras</p>
                        <p className="text-xl font-bold text-orange-600">{formatCurrency(purchases?.summary?.total)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Promedio</p>
                        <p className="text-xl font-bold">{formatCurrency(purchases?.summary?.avg_purchase)}</p>
                      </div>
                    </div>

                    {/* Top Suppliers */}
                    {purchases?.top_suppliers && purchases.top_suppliers.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                          <Truck className="w-4 h-4" />
                          Top Proveedores
                        </h4>
                        <div className="space-y-2">
                          {purchases.top_suppliers.slice(0, 5).map((supplier, idx) => (
                            <div key={supplier.name} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx < 3 ? 'bg-orange-500 text-white' : 'bg-slate-200'
                                }`}>
                                  {idx + 1}
                                </span>
                                <span className="text-sm font-medium truncate max-w-[150px]">{supplier.name}</span>
                              </div>
                              <span className="font-bold text-orange-600">{formatCurrency(supplier.total)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* By Payment Method */}
                    {purchases?.by_payment_method && purchases.by_payment_method.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                          <Wallet className="w-4 h-4" />
                          Por Método de Pago
                        </h4>
                        <div className="flex gap-2 flex-wrap">
                          {purchases.by_payment_method.map((method, idx) => (
                            <div key={method.method} className="flex-1 min-w-[100px] p-3 bg-slate-50 rounded-lg text-center">
                              <p className="text-lg font-bold text-orange-600">{formatCurrency(method.total)}</p>
                              <p className="text-xs text-muted-foreground">{method.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!purchases?.summary?.total || purchases.summary.total === 0) && (
                      <div className="text-center py-6 text-muted-foreground">
                        <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No hay compras en este período</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* TRASPASOS Card */}
                <Card className="shadow-sm border-0 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-bold">
                      <Boxes className="w-6 h-6" />
                      Traspasos de Inventario
                    </CardTitle>
                    <p className="text-blue-100 text-sm">
                      {transfers?.summary?.count || 0} traspasos realizados
                    </p>
                  </CardHeader>
                  <CardContent className="p-5 space-y-4">
                    {/* Transfers Summary */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Total Unidades</p>
                        <p className="text-xl font-bold text-blue-600">{transfers?.summary?.total_units?.toFixed(1) || 0}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Insumos Diferentes</p>
                        <p className="text-xl font-bold">{transfers?.summary?.unique_supplies || 0}</p>
                      </div>
                    </div>

                    {/* By Origin/Destination */}
                    {transfers?.by_route && transfers.by_route.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                          <ChevronRight className="w-4 h-4" />
                          Rutas de Traspaso
                        </h4>
                        <div className="space-y-2">
                          {transfers.by_route.slice(0, 5).map((route, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium">{route.from}</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{route.to}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">{route.count} traspasos</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Transferred Supplies */}
                    {transfers?.top_supplies && transfers.top_supplies.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          Insumos Más Traspasados
                        </h4>
                        <div className="space-y-2">
                          {transfers.top_supplies.slice(0, 5).map((supply, idx) => (
                            <div key={supply.supply_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="text-sm font-medium truncate max-w-[180px]">{supply.name}</span>
                              <span className="font-bold text-blue-600">{supply.total_qty.toFixed(1)} {supply.unit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!transfers?.summary?.count || transfers.summary.count === 0) && (
                      <div className="text-center py-6 text-muted-foreground">
                        <Boxes className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No hay traspasos en este período</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* GASTOS OPERATIVOS Card */}
                <Card className="shadow-sm border-0 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-purple-500 to-violet-600 text-white pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-bold">
                      <FileText className="w-6 h-6" />
                      Gastos Operativos
                    </CardTitle>
                    <p className="text-purple-100 text-sm">
                      {expenses?.summary?.count || 0} gastos registrados
                    </p>
                  </CardHeader>
                  <CardContent className="p-5 space-y-4">
                    {/* Expenses Summary */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Total Gastos</p>
                        <p className="text-xl font-bold text-purple-600">{formatCurrency(expenses?.summary?.total)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Promedio</p>
                        <p className="text-xl font-bold">{formatCurrency(expenses?.summary?.avg_expense)}</p>
                      </div>
                    </div>

                    {/* By Category */}
                    {expenses?.by_category && expenses.by_category.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Por Categoría
                        </h4>
                        <div className="space-y-2">
                          {expenses.by_category.map((cat, idx) => (
                            <div key={cat.category} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                <span className="text-sm font-medium">{cat.label}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-purple-600">{formatCurrency(cat.total)}</span>
                                <span className="text-xs text-muted-foreground ml-2">({cat.percentage}%)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Budget vs Actual */}
                    {expenses?.budget_comparison && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Presupuesto vs Real
                        </h4>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-muted-foreground">Presupuesto</p>
                            <p className="font-bold">{formatCurrency(expenses.budget_comparison.budget)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Real</p>
                            <p className="font-bold text-purple-600">{formatCurrency(expenses.budget_comparison.actual)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Diferencia</p>
                            <p className={`font-bold ${expenses.budget_comparison.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(expenses.budget_comparison.difference)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {(!expenses?.summary?.total || expenses.summary.total === 0) && (
                      <div className="text-center py-6 text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No hay gastos en este período</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

              </div>

              {/* Sales Trend Chart (Full Width) */}
              {sales?.trend_data && sales.trend_data.length > 0 && (
                <Card className="mt-6 shadow-sm border-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-bold flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-accent" />
                      Tendencia de Ventas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sales.trend_data}>
                          <defs>
                            <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                          <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Area 
                            type="monotone" 
                            dataKey="sales" 
                            stroke="#10b981" 
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorSales)"
                            name="Ventas"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportsHub;
